import {
  ArrowLeftRight,
  CalendarClock,
  Check,
  ClipboardList,
  DollarSign,
  Download,
  Folder,
  Gift,
  LifeBuoy,
  MessageSquare,
  Pause,
  Play,
  RotateCcw,
  ShieldCheck,
  Star,
  UserPlus,
  Users,
  X,
} from "lucide-react"

export const ORDER_MODAL_KEYS = Object.freeze({
  PAY_NOW: "pay-now",
  REVIEW_REQUESTS: "review-requests",
})

// One catalog entry per customer action. `perm` names the permission flag (from
// OrderDetailPresenter#permissions) that must be true for the action to surface
// — null means always available (navigation or support). `modal` opens a dialog,
// `tab` jumps the rail, `download` pulls the delivered file. Mirrors ACT in the
// design prototype (shared/shell.js), made permission-first so the header never
// offers a button the backend would reject.
export const ACTIONS = {
  pay:             { label: "Pay now",             Icon: DollarSign,     modal: ORDER_MODAL_KEYS.PAY_NOW, perm: "payNow" },
  message:         { label: "Message your team",   Icon: MessageSquare,  tab: "chat",               perm: null },
  invite:          { label: "Invite a freelancer", Icon: UserPlus,       href: "/find-freelancers", perm: null },
  approveDraft:    { label: "Approve draft",       Icon: Check,          modal: "approve-draft",    perm: "approveDraft" },
  requestRevision: { label: "Request changes",     Icon: RotateCcw,      modal: "request-revision", perm: "requestRevision" },
  reviewScan:      { label: "Review scan",         Icon: ShieldCheck,    modal: "review-scan",      perm: "reviewScan" },
  reviewExtension: { label: "Review extension",     Icon: CalendarClock,  modal: "review-extension", perm: "reviewExtension" },
  approveDesign:   { label: "Approve design",      Icon: Check,          modal: "approve-design",   perm: "approveDesign" },
  approveSample:   { label: "Approve sample",      Icon: Check,          modal: "approve-sample",   perm: "reviewSample" },
  requestSample:   { label: "Request another",     Icon: RotateCcw,      modal: "request-another-sample", perm: "reviewSample" },
  approveFinal:    { label: "Approve & complete",  Icon: Check,          modal: "approve-final",    perm: "approveFinal" },
  resume:          { label: "Resume project",      Icon: Play,           modal: "resume",           perm: "resume" },
  download:        { label: "Download",            Icon: Download,       download: true,            perm: "download" },
  hold:            { label: "Hold project",        Icon: Pause,          modal: "hold",             perm: "hold" },
  reallocate:      { label: "Reallocate order",    Icon: ArrowLeftRight, modal: "reallocate",       perm: "reallocate" },
  manageAccess:    { label: "Manage access",       Icon: UserPlus,       modal: "manage-access",    perm: "manageAccess" },
  tip:             { label: "Tip your freelancer", Icon: Gift,           modal: "tip",              perm: "tip" },
  rate:            { label: "Leave a rating",      Icon: Star,           modal: "rate",             perm: "rate" },
  reviewRequests:  { label: "Review requests",     Icon: Users,          modal: ORDER_MODAL_KEYS.REVIEW_REQUESTS, perm: null },
  details:         { label: "Order details",       Icon: ClipboardList,  tab: "details",            perm: null },
  files:           { label: "Shared files",        Icon: Folder,         tab: "files",              perm: null },
  support:         { label: "Contact support",     Icon: LifeBuoy,       modal: "support",          perm: null },
  cancel:          { label: "Cancel order",        Icon: X,              modal: "cancel",           perm: "cancel", danger: true },
}

// What the Download menu calls each file, keyed by the `kind` the server sends
// (OrderDetailPresenter#download_files). A kind with no label falls back to
// the file's own name.
export const DOWNLOAD_LABELS = {
  revision_content: "Revised file",
  zipped_deliverables: "Final writing files",
  final_content: "Final document",
  paperback_format: "Paperback interior file",
  covers_zipped: "Finished covers",
  illustrations_zipped: "Finished illustrations",
  narration_zip_file: "All audio files",
  ebook_cover: "eBook cover",
  audio_cover: "Audiobook cover",
  paperback_cover: "Paperback cover",
  cover_source_file: "Cover source files",
  cover_design_elements: "Cover design elements",
  illustrations_source_file: "Editable illustration files",
  copyright_form: "Copyright transfer form",
}

// The files Download offers. One file that is the order's own archive is a
// plain button; anything else is a menu, so a lone copyright form or source
// file is named rather than handed over unlabelled.
export function downloadFiles(order) {
  return order.deliverables?.downloads ?? []
}

export function downloadIsMenu(order) {
  const files = downloadFiles(order)
  return files.length > 1 || (files.length === 1 && !files[0].main)
}

// Phase -> intended primary + outline secondaries (design prototype PHASES).
// Approve only appears in the four approval phases; every other phase gets its
// own primary. Resolution filters these by real permission and falls back to
// Message, so the cluster is never empty or wrong.
const PHASE_PRIMARY = {
  payment:     { primary: "pay",           secondary: [] },
  scheduled:   { primary: "message",       secondary: [] },
  production:  { primary: "message",       secondary: [] },
  draft:       { primary: "approveDraft",  secondary: ["requestRevision"] },
  scan:        { primary: "reviewScan",    secondary: [] },
  design:      { primary: "approveDesign", secondary: ["requestRevision"] },
  final:       { primary: ["approveFinal", "approveSample"], secondary: ["download", "requestRevision", "requestSample"] },
  staffReview: { primary: "message",       secondary: [] },
  finalizing:  { primary: "message",       secondary: [] },
  completed:   { primary: "download",      secondary: ["tip"] },
  hold:        { primary: "resume",        secondary: ["message"] },
  cancelled:   { primary: "message",       secondary: [] },
}

// The ••• overflow, grouped (this order · manage · support · destructive). Each
// item shows only when available; empty groups drop out and a separator is drawn
// between the survivors.
const MENU_GROUPS = [
  ["details", "files", "manageAccess"],
  ["hold", "reallocate"],
  ["tip", "rate", "support"],
  ["cancel"],
]

function available(key, order) {
  const action = ACTIONS[key]
  if (!action) return false
  // A delivered file is offered only when the server both sent it and grants
  // the download permission (Order#show_download_button?); either alone is
  // not enough.
  if (action.download) {
    return Boolean((order.deliverables?.download || downloadFiles(order).length) && order.permissions?.download)
  }
  if (action.perm == null) return true

  return Boolean(order.permissions?.[action.perm])
}

function entry(key) {
  return { key, ...ACTIONS[key] }
}

// The Find Freelancers link the invite action targets (pattern:
// IllustrationsPanel's role-targeted link). The role facet is server-derived
// (OrderDetailPresenter#invite_role reads the real package predicates the
// frontend type key can't distinguish); orders with no matching public role
// (e.g. formatting) carry null and link to the unfiltered roster. `niche` is
// the one qualification the order demands of that role, when it demands one
// (OrderDetailPresenter#invite_niche: Plagiarism Pro on a plagiarism order),
// pre-applied as the page's own `niches` filter (UPDA-103). `order` carries
// the order the customer launched from so the invite dialog can open on that
// project — a hint only: Find Freelancers matches it against the projects the
// dialog loads and ignores anything it can't select.
export function inviteHref(role, orderId, niche = null) {
  const params = []
  if (role) params.push(`role=${role}`)
  if (niche) params.push(`niches=${niche}`)
  if (orderId) params.push(`order=${orderId}`)

  return params.length ? `/find-freelancers?${params.join("&")}` : "/find-freelancers"
}

// Phases where "Invite a freelancer" makes no sense even with nobody assigned —
// the work is over (or paused), so there is nobody to send the customer to.
const INVITE_EXCLUDED_PHASES = ["completed", "cancelled", "hold"]

// Before anyone is assigned there is no team to message, so the Message
// fallback would land the customer in an empty chat (AT-03). Steer them to
// Find Freelancers instead — unless payment is possible (Pay keeps priority;
// the backend legitimately allows pre-assignment payment) or the order is
// terminal. Any team member at all counts as assigned.
function inviteApplies(order) {
  return !order.team?.length &&
    !INVITE_EXCLUDED_PHASES.includes(order.phase) &&
    !available("pay", order)
}

// What "Message your team" resolves to when a phase names it. A seat the
// order is waiting on comes first, writer or not: the server emits `openSeat`
// only when this viewer's invite would be accepted, and the header then says
// "Invite an editor" (UPDA-105). Otherwise Message reaches a team that exists;
// before anyone is assigned it is Find Freelancers, and when the work is
// paused or over with nobody to invite it is the order's details — never a
// chat with nobody in it (UPDA-106).
function messageFallback(order) {
  if (order.openSeat) return "invite"
  if (order.team?.length) return "message"
  if (inviteApplies(order)) return "invite"
  return "details"
}

// The Invite entry, aimed at the seat the order is waiting on when there is
// one (Find Freelancers filtered to that role, this project pre-selected), and
// at the order's own role facet before any assignment. Either way the order's
// required qualification rides along, so a plagiarism order's link lands on
// Plagiarism Pro editors whichever state it is in.
function inviteEntry(order) {
  const seat = order.openSeat
  const niche = order.inviteNiche
  if (seat) return { ...entry("invite"), label: seat.label, href: inviteHref(seat.role, order.id, niche) }

  return { ...entry("invite"), href: inviteHref(order.inviteRole, order.id, niche) }
}

function reviewRequestsLabel(count) {
  return `Review ${count} ${count === 1 ? "request" : "requests"}`
}

// Freelancer requests the customer can still answer. Only resolveHeader offers
// this action: its catalog entry has no permission flag, so it must never be
// listed in PHASE_PRIMARY or MENU_GROUPS.
function reviewRequestsEntry(order) {
  const waiting = (order.requestItems ?? []).filter((request) => request.canApprove || request.canDecline)
  if (!waiting.length) return null

  return { ...entry("reviewRequests"), label: reviewRequestsLabel(waiting.length) }
}

// Resolve the header action cluster for an order: { primary, secondary[], menu[] }
// where menu is an array of groups (each a list of entries).
export function resolveHeader(order) {
  // A collaborator the customer shared the order with (AU-44) gets no action
  // cluster at all — not even the always-available Message/Support entries,
  // which lead to the owner's conversation and the owner's support endpoint.
  if (order.viewer?.readOnly) return { primary: null, secondary: [], menu: [] }

  const conf = PHASE_PRIMARY[order.phase] || PHASE_PRIMARY.production
  // `primary` may name several candidates: the final phase covers both the
  // finished-content approval and a narration sample verdict, and only one of
  // them is ever permitted on a given order. First available wins.
  let primaryKey = [].concat(conf.primary).find((key) => available(key, order)) ?? "message"
  if (primaryKey === "message") primaryKey = messageFallback(order)

  // A secondary Message follows the same rule as the primary: it is never
  // offered to an empty team. Order details already sits in the menu, so the
  // entry is dropped rather than doubled.
  const secondary = conf.secondary
    .filter((key) => key !== primaryKey && available(key, order))
    .filter((key) => key !== "message" || order.team?.length)
    .map(entry)

  // A pending extension needs the customer's call regardless of phase — surface
  // it as the lead secondary action whenever one is waiting.
  if (primaryKey !== "reviewExtension" && available("reviewExtension", order)) {
    secondary.unshift(entry("reviewExtension"))
  }

  const menu = MENU_GROUPS.map((group) =>
    group.filter((key) => key !== primaryKey && available(key, order)).map(entry)
  ).filter((group) => group.length > 0)

  let primary = primaryKey === "invite" ? inviteEntry(order) : entry(primaryKey)

  // Freelancers are waiting on the customer's pick. Pay keeps the lead (most
  // services can be paid before anyone is chosen) with the review beside it;
  // otherwise the review leads and what would have led sits beside it.
  const review = reviewRequestsEntry(order)
  if (review && primary.key === "pay") {
    secondary.push(review)
  } else if (review) {
    secondary.push(primary)
    primary = review
  }

  return { primary, secondary, menu }
}
